import {
  isValidElement,
  useEffect,
  useMemo,
  useState,
  type ComponentType,
  type ReactNode,
} from "react";
import { normalizeSvgSrc } from "utils/normalize-svg-src";

type MediaSrcProp =
  | string
  | { readonly src: string }
  | ComponentType<{ className?: string; width?: number; height?: number }>;

export interface MediaImageProps {
  readonly src: MediaSrcProp;
  readonly width?: number;
  readonly height?: number;
  readonly onClick?: () => void;
  readonly className?: string;
  readonly alt?: string;
}

function isSvgComponent(
  src: unknown
): src is ComponentType<{ className?: string; width?: number; height?: number }> {
  return typeof src === "function";
}

function isLocalSvgSrc(src: string) {
  return src.startsWith("/") && src.toLowerCase().endsWith(".svg");
}

function isIconSized(width?: number, height?: number) {
  const w = width ?? 100;
  const h = height ?? 100;
  return w <= 64 && h <= 64;
}

export function MediaImage({
  width,
  height,
  src,
  onClick,
  className,
  alt = "",
}: MediaImageProps) {
  const resolvedWidth = width ?? 100;
  const resolvedHeight = height ?? 100;

  if (isValidElement(src)) {
    return src as ReactNode;
  }

  if (isSvgComponent(src)) {
    const SvgComponent = src;
    return (
      <SvgComponent
        width={resolvedWidth}
        height={resolvedHeight}
        className={className}
      />
    );
  }

  const resolvedSrc = normalizeSvgSrc(src);
  const eager =
    isLocalSvgSrc(resolvedSrc) || isIconSized(resolvedWidth, resolvedHeight);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [resolvedSrc]);

  const imgElement = useMemo(
    () => {
      if (!resolvedSrc || failed) return null;

      return (
        <img
          src={resolvedSrc}
          onClick={onClick}
          className={[onClick ? "cursor-pointer" : "", className]
            .filter(Boolean)
            .join(" ")}
          width={resolvedWidth}
          height={resolvedHeight}
          alt={alt}
          loading={eager ? "eager" : "lazy"}
          decoding="async"
          draggable={false}
          onError={() => setFailed(true)}
        />
      );
    },
    [
      resolvedSrc,
      failed,
      onClick,
      className,
      resolvedWidth,
      resolvedHeight,
      alt,
      eager,
    ]
  );

  return imgElement;
}

export default MediaImage;
